import React, { useEffect, useMemo, useState } from 'react';
import { Minus, Plus, Trash2, X } from 'lucide-react';
import { formatPrice } from '../../utils/formatters';
import { clampDiscount } from '../../utils/pricing';
import { getSupplementVariantIds } from '../../utils/supplements';
import { capitalize } from '../../utils/strings';
import { useModalScrollLock } from '../../components/funnel/useModalScrollLock';
import { normalizeDistributionSignature } from '../../utils/recipeDistribution';
import { BreedPhoto } from '../../components/funnel/BreedPhoto';
import type { DonationAmount } from '../../hooks/useDonationConfig';
import type { SupplementConfig } from '../../types/config';
import type { CheckoutRecipeLine } from '../../checkout/useCheckoutPricing';
import type { PetSnapshot } from '../../types/pets';
import type { Recipe } from '../../types';
import { stockLimitFor, type StockLimits } from '../../checkout/stockLimits';

type TFn = (key: string, fallback: string, params?: Record<string, string | number>) => string;

interface CartSummaryProps {
  t: TFn;
  isTransition: boolean;
  transitionScheduleDays: number;
  INITIATION_DAYS: number;
  SUBSCRIPTION_DAYS: number;
  INITIATION_PACK_DISCOUNT: number;
  MONTHLY_SUBSCRIPTION_DISCOUNT: number;
  initiationPrice: number;
  initiationOriginalPrice: number;
  monthlyDiscountedPrice: number;
  supplementsOneOffTotal: number;
  todayTotal: number;
  shippingPrice: number;
  shippingUnavailable?: boolean;
  /** No postal code yet for a country with zones: ask for the address instead of a price. */
  shippingAddressPending?: boolean;
  /** Compact country + postal code control rendered under the shipping line. */
  shippingControl?: React.ReactNode;
  monthlyShippingFree?: boolean;
  monthlyShippingPrice?: number;
  discountAmount: number;
  dailyCost: number;
  savings: number;
  effectiveRecipeLines: CheckoutRecipeLine[];
  hasSupplements: boolean;
  selectedSupplements: string[];
  supplementRules: SupplementConfig[];
  donateRecurringAmount: DonationAmount | null;
  donateOneTimeAmount: DonationAmount | null;
  donateCustomAmount: string;
  donationMode: 'recurring' | 'one-time';
  updateFormData: (data: Record<string, unknown>) => void;
  onEditRecipes?: () => void;
  currentDistribution?: Record<string, number>;
  recommendedDistribution?: Record<string, number>;
  onUpdateCurrentDistribution?: (distribution: Record<string, number>) => void;
  onEditCompletedPetPack?: (petId: string) => void;
  onRemoveCompletedPetPack?: (petId: string) => void;
  onRemoveCurrentPetPack?: () => void;
  // Multi-pet props
  isMultiPet?: boolean;
  petsCompleted?: PetSnapshot[];
  currentNombre?: string;
  currentRaza?: string | null;
  recipes?: Recipe[];
  multiPetTotals?: { totalInitiation: number; totalMonthly: number };
  /** productId → max units for the first shipment (absent = no limit, 0 = sold out). */
  stockLimits?: StockLimits;
  /** Fired when the edit-menus modal opens (Step13 refreshes the config snapshot). */
  onOpenEditMenus?: () => void;
  /** pack_rules.allow_recipe_removal_in_checkout: shows the trash button per line. */
  canRemoveRecipes?: boolean;
}

// Distribution keys are canonical product.ids (UUIDs); match recipes by productId.
function findRecipeByProductId(recipes: Recipe[], productId: string): Recipe | undefined {
  return recipes.find(
    (r) =>
      String(r.productId) === productId ||
      (r.variants && Object.values(r.variants).some((v) => String(v.productId) === productId)),
  );
}

function buildPetRecipeLines(
  distribution: Record<string, number>,
  recipes: Recipe[],
  discount: number,
  storedNames?: Record<string, string>,
): CheckoutRecipeLine[] {
  return Object.entries(distribution)
    .filter(([, qty]) => Number.isFinite(qty) && qty > 0)
    .map(([productId, qty]) => {
      const recipe = findRecipeByProductId(recipes, productId);
      const matchedVariantEntry = recipe?.variants
        ? Object.entries(recipe.variants).find(([, v]) => String(v.productId) === productId)
        : undefined;
      const matchedVariant = matchedVariantEntry?.[1];
      const sizeSuffix = matchedVariantEntry?.[0] ?? (recipe?.packSizeG ? `${recipe.packSizeG}g` : '');
      const baseName = recipe?.name ?? storedNames?.[productId] ?? productId;
      const unitPrice = matchedVariant?.priceEur ?? recipe?.pricePerUnit ?? 0;
      const name = sizeSuffix ? `${baseName} ${sizeSuffix}` : baseName;
      return {
        productId: productId,
        name,
        qty,
        originalTotal: Number((qty * unitPrice).toFixed(2)),
        discountedTotal: Number((qty * unitPrice * (1 - discount)).toFixed(2)),
        imageUrl: recipe?.imageUrl,
        proteinType: recipe?.proteinType,
      };
    });
}

function discountForStart(start: unknown, initiationDiscount: number, monthlyDiscount: number): number {
  return start === 'full' ? clampDiscount(monthlyDiscount) : clampDiscount(initiationDiscount);
}

function roundedDiscount(discount: number): number {
  return Math.round(discount * 10000) / 10000;
}

function ensurePetNameInLabel(label: string, nombre: string): string {
  const cleanName = nombre.trim();
  if (!cleanName) return label;
  return label.toLowerCase().includes(cleanName.toLowerCase()) ? label : `${label} de ${cleanName}`;
}

function RecipeLinesList({ lines, t }: { lines: CheckoutRecipeLine[]; t: TFn }) {
  if (lines.length === 0) {
    return (
      <p className="font-poppins text-xs text-black/40 mt-3 text-center italic">
        {t('step13c.no_recipe_details', 'No hay recetas seleccionadas. Vuelve a "Recetas" para configurarlas.')}
      </p>
    );
  }
  return (
    <div className="space-y-2 mt-3">
      {lines.map((line, idx) => {
        const sizeMatch = line.name.match(/(400g|800g)$/);
        const sizeSuffix = sizeMatch ? sizeMatch[1].replace('g', 'gr') : '';
        const protein = capitalize(line.proteinType ?? '');
        const cleanName = sizeSuffix ? line.name.replace(/\s*\d+g\s*$/, '').trim() : line.name;
        // If the clean name is just the protein type (e.g. "Cerdo"), prefix it
        const isJustProtein = protein && cleanName.toLowerCase() === protein.toLowerCase();
        const menuName =
          cleanName && cleanName !== line.productId && !isJustProtein
            ? cleanName
            : protein
              ? t('step13c.menu_of', 'Menú de {protein}', { protein })
              : line.name;
        return (
          <div key={idx} className="flex items-center gap-3 bg-gray-50 rounded-lg px-3 py-2">
            <div className="w-10 h-10 rounded-md bg-white overflow-hidden flex-shrink-0 flex items-center justify-center p-0.5">
              {line.imageUrl ? (
                <img src={line.imageUrl} alt={menuName} className="w-full h-full object-contain" />
              ) : (
                <span className="text-xl">🍖</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <span className="font-poppins text-sm text-black font-semibold block truncate">{menuName}</span>
              <span className="font-poppins text-xs text-black/40">
                x{line.qty}
                {sizeSuffix && ` ${sizeSuffix}`}
              </span>
            </div>
            <div className="text-right flex-shrink-0">
              {line.originalTotal !== line.discountedTotal && (
                <span className="block font-poppins text-[11px] text-black/30 line-through">
                  {formatPrice(line.originalTotal)}
                </span>
              )}
              <span className="font-fredoka text-sm font-bold text-black">{formatPrice(line.discountedTotal)}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface VariantMeta {
  recipe: Recipe;
  productId: string;
  sizeLabel: string;
  packSizeG: number;
  priceEur: number;
}

function getVariantMeta(recipes: Recipe[], productId: string): VariantMeta | null {
  const recipe = findRecipeByProductId(recipes, productId);
  if (!recipe) return null;
  const variantEntry = recipe.variants
    ? Object.entries(recipe.variants).find(([, variant]) => String(variant.productId) === String(productId))
    : undefined;
  if (variantEntry) {
    const sizeLabel = variantEntry[0];
    return {
      recipe,
      productId: productId,
      sizeLabel,
      packSizeG: Number(sizeLabel.replace('g', '')) || recipe.packSizeG || recipe.servingSize || 400,
      priceEur: variantEntry[1].priceEur,
    };
  }
  const packSizeG = recipe.packSizeG || recipe.servingSize || 400;
  return {
    recipe,
    productId: productId,
    sizeLabel: `${packSizeG}g`,
    packSizeG,
    priceEur: recipe.pricePerUnit,
  };
}

function kcalForDistribution(distribution: Record<string, number> | undefined, recipes: Recipe[]): number {
  return Object.entries(distribution || {}).reduce((sum, [productId, qty]) => {
    const meta = getVariantMeta(recipes, productId);
    if (!meta || !Number.isFinite(qty) || qty <= 0) return sum;
    return sum + qty * meta.packSizeG * (meta.recipe.kcalPer100g / 100);
  }, 0);
}

function formatKcal(value: number): string {
  const rounded = String(Math.round(value)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${rounded} kcal`;
}

const MAX_UNITS = 99;

function clampQty(value: number, max?: number | null): number {
  const ceiling = max != null && max >= 1 ? Math.min(max, MAX_UNITS) : MAX_UNITS;
  return Math.max(1, Math.min(ceiling, Math.round(value)));
}

// Clamps every positive line to its product limit; a limit of 0 cannot be expressed
// (the modal has no 0), so that line keeps its value and is flagged as sold out instead.
function clampDistribution(distribution: Record<string, number>, limits?: StockLimits): Record<string, number> {
  return Object.fromEntries(
    Object.entries(distribution).map(([productId, qty]) =>
      Number.isFinite(qty) && qty > 0 ? [productId, clampQty(qty, stockLimitFor(limits, productId))] : [productId, qty],
    ),
  );
}

interface EditMenusModalProps {
  t: TFn;
  recipes: Recipe[];
  initialDistribution: Record<string, number>;
  recommendedDistribution?: Record<string, number>;
  onClose: () => void;
  onApply: (distribution: Record<string, number>) => void;
  raza?: string | null;
  nombre?: string;
  packDays: number;
  discount: number;
  onGoToRecipes?: () => void;
  stockLimits?: StockLimits;
  canRemoveLines: boolean;
}

function EditMenusModal({
  t,
  recipes,
  initialDistribution,
  recommendedDistribution,
  onClose,
  onApply,
  raza,
  nombre,
  packDays,
  discount,
  onGoToRecipes,
  stockLimits,
  canRemoveLines,
}: EditMenusModalProps) {
  useModalScrollLock();
  const [draft, setDraft] = useState<Record<string, number>>(() => clampDistribution(initialDistribution, stockLimits));
  const lines = useMemo(
    () =>
      Object.entries(draft)
        .filter(([, qty]) => Number.isFinite(qty) && qty > 0)
        .map(([productId, qty]) => ({ productId, qty: clampQty(qty), meta: getVariantMeta(recipes, productId) }))
        .filter((line): line is { productId: string; qty: number; meta: VariantMeta } => Boolean(line.meta)),
    [draft, recipes],
  );
  // A dropping limit while the modal is open (config refresh) re-clamps the draft.
  useEffect(() => {
    setDraft((prev) => {
      const next = clampDistribution(prev, stockLimits);
      const changed = Object.keys(next).some((productId) => next[productId] !== prev[productId]);
      return changed ? next : prev;
    });
  }, [stockLimits]);

  // Nothing to apply until the draft differs from the pack on screen.
  const hasChanges = useMemo(
    () => normalizeDistributionSignature(draft) !== normalizeDistributionSignature(initialDistribution),
    [draft, initialDistribution],
  );
  // «Restaurar original» as soon as the draft (applied or not) differs from the recommendation.
  const canRestore = useMemo(
    () =>
      Boolean(recommendedDistribution) &&
      normalizeDistributionSignature(draft) !== normalizeDistributionSignature(recommendedDistribution),
    [draft, recommendedDistribution],
  );
  const hasSoldOutLine = useMemo(
    () => lines.some(({ productId }) => stockLimitFor(stockLimits, productId) === 0),
    [lines, stockLimits],
  );
  const currentKcal = useMemo(() => kcalForDistribution(draft, recipes), [draft, recipes]);
  const recommendedKcal = useMemo(
    () => kcalForDistribution(recommendedDistribution || initialDistribution, recipes),
    [initialDistribution, recommendedDistribution, recipes],
  );
  const kcalPct = recommendedKcal > 0 ? Math.round((currentKcal / recommendedKcal) * 100) : 100;
  const kcalTone = kcalPct < 90 || kcalPct > 110 ? 'text-amber-600' : 'text-[#008BBC]';
  const totalOriginal = useMemo(() => lines.reduce((sum, { qty, meta }) => sum + qty * meta.priceEur, 0), [lines]);
  const totalDiscounted = useMemo(
    () => lines.reduce((sum, { qty, meta }) => sum + qty * meta.priceEur * (1 - discount), 0),
    [lines, discount],
  );

  const updateQty = (productId: string, nextQty: number) => {
    setDraft((prev) => ({ ...prev, [productId]: clampQty(nextQty, stockLimitFor(stockLimits, productId)) }));
  };

  const removeLine = (productId: string) => {
    setDraft((prev) => {
      const active = Object.entries(prev).filter(([, qty]) => Number.isFinite(qty) && qty > 0);
      if (active.length <= 1) return prev;
      const next = { ...prev };
      delete next[productId];
      return next;
    });
  };

  const isSizeSoldOut = (recipe: Recipe, size: '400g' | '800g'): boolean => {
    const variant = recipe.variants?.[size];
    if (!variant) return true;
    if (variant.isOutOfStock) return true;
    return stockLimitFor(stockLimits, String(variant.productId)) === 0;
  };

  const changeSize = (productId: string, nextSize: '400g' | '800g') => {
    const meta = getVariantMeta(recipes, productId);
    if (!meta || isSizeSoldOut(meta.recipe, nextSize)) return;
    // The distribution is keyed by product.id (UUID): switch to the target
    // size's productId, not its Shopify variant id.
    const rawNextId = meta.recipe.variants?.[nextSize]?.productId;
    const nextProductId = rawNextId != null ? String(rawNextId) : '';
    if (!nextProductId || nextProductId === productId) return;
    setDraft((prev) => {
      const qty = clampQty(prev[productId] || 1, stockLimitFor(stockLimits, nextProductId));
      // Rebuild preserving insertion order so the card stays in its position
      return Object.fromEntries(
        Object.entries(prev).map(([id, q]) => (id === productId ? [nextProductId, qty] : [id, q])),
      );
    });
  };

  const resetDraft = () => {
    if (recommendedDistribution) setDraft(clampDistribution({ ...recommendedDistribution }, stockLimits));
  };

  const apply = () => {
    if (hasSoldOutLine) return;
    const cleaned = Object.fromEntries(
      Object.entries(draft)
        .filter(([, qty]) => Number.isFinite(qty) && qty > 0)
        .map(([productId, qty]) => [productId, clampQty(qty, stockLimitFor(stockLimits, productId))]),
    );
    if (Object.keys(cleaned).length === 0) return;
    onApply(cleaned);
  };

  // Close on Escape, like the other modals of the checkout.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      data-testid="edit-menus-backdrop"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 px-3 py-4"
      onClick={(event) => {
        // Only a click on the backdrop itself closes: clicks inside the dialog bubble up here too.
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-menus-title"
        className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white shadow-2xl"
      >
        <div className="sticky top-0 z-10 flex items-start gap-3 border-b border-black/5 bg-white px-4 py-2.5">
          {raza && <BreedPhoto breedKey={raza} className="w-10 h-10 flex-shrink-0 rounded-full" alt={nombre ?? ''} />}
          <div className="min-w-0 flex-1">
            <h3 id="edit-menus-title" className="font-fredoka text-lg font-bold text-[#1a1a1a]">
              {t('step13c.edit_menus_title', 'Editar menú')}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-black/5 text-black/50 hover:bg-black/10"
            aria-label={t('step13c.close', 'Cerrar')}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div className="space-y-2.5 px-4 pt-3">
          <div className="rounded-xl bg-[#FAF7F1] p-3">
            <div className="flex items-baseline justify-between gap-3">
              <span className="font-poppins text-xs font-medium text-black/50">
                {t('step13c.edit_menus_kcal_label', 'Kcal del pedido')}
              </span>
              <span className={`font-fredoka text-lg font-black ${kcalTone}`}>{formatKcal(currentKcal)}</span>
            </div>
            <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-black/10">
              <div
                className={`h-full rounded-full transition-all duration-300 ${kcalPct < 90 || kcalPct > 110 ? 'bg-amber-400' : 'bg-[#008BBC]'}`}
                style={{ width: `${Math.min(100, kcalPct)}%` }}
              />
            </div>
            <div className="mt-1.5 flex items-center justify-between gap-3 font-poppins text-[11px] text-black/45">
              <span>
                {t('step13c.edit_menus_kcal_target', 'Recomendado: {kcal}', { kcal: formatKcal(recommendedKcal) })}
              </span>
              <span className={kcalTone}>{kcalPct}%</span>
            </div>
          </div>

          {recommendedKcal > 0 && packDays > 0 && (
            <div className="flex items-baseline justify-between gap-3 rounded-xl bg-[#FAF7F1] px-3 py-2">
              <span className="font-fredoka text-base font-bold text-[#1a1a1a]">
                {t('step13c.edit_menus_days_covered', 'Cubre aprox. {days} días', {
                  days: Math.round((currentKcal / recommendedKcal) * packDays),
                })}
              </span>
              <span className="font-poppins text-[11px] text-black/40">
                {t('step13c.edit_menus_days_expected', '(plan: {days} días)', { days: packDays })}
              </span>
            </div>
          )}

          <div className="space-y-1.5">
            {lines.map(({ productId, qty, meta }) => {
              const canChangeSize = Boolean(meta.recipe.variants?.['400g'] && meta.recipe.variants?.['800g']);
              const rawName = meta.recipe.name ?? '';
              const proteinType = meta.recipe.proteinType ? capitalize(meta.recipe.proteinType) : '';
              const isJustProtein = proteinType && rawName.toLowerCase() === proteinType.toLowerCase();
              const lineName =
                rawName && rawName !== productId && !isJustProtein
                  ? rawName
                  : proteinType
                    ? t('step13c.menu_of', 'Menú de {protein}', { protein: proteinType })
                    : rawName || productId;
              const unitOriginal = meta.priceEur;
              const unitDiscounted = unitOriginal * (1 - discount);
              const lineOriginal = qty * unitOriginal;
              const lineDiscounted = qty * unitDiscounted;
              const limit = stockLimitFor(stockLimits, productId);
              const isSoldOut = limit === 0;
              const atLimit = limit != null && limit > 0 && qty >= limit;
              return (
                <div key={productId} className="rounded-xl border border-gray-100 bg-white p-2.5 shadow-sm">
                  <div className="flex items-start gap-3">
                    <div className="h-11 w-11 shrink-0 overflow-hidden rounded-lg bg-[#FAF7F1] p-1">
                      {meta.recipe.imageUrl ? (
                        <img src={meta.recipe.imageUrl} alt={lineName} className="h-full w-full object-contain" />
                      ) : (
                        <span className="flex h-full w-full items-center justify-center text-xl">🍖</span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-poppins text-sm font-bold text-black">{lineName}</p>
                      <div
                        data-testid="edit-line-meta"
                        className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 font-poppins text-[11px]"
                      >
                        <span className="text-black/40">
                          {formatKcal(qty * meta.packSizeG * (meta.recipe.kcalPer100g / 100))}
                        </span>
                        {discount > 0 && (
                          <span className="text-black/30 line-through">{formatPrice(unitOriginal)}/ud</span>
                        )}
                        <span className="font-semibold text-[#008BBC]">{formatPrice(unitDiscounted)}/ud</span>
                      </div>
                    </div>
                    {canRemoveLines && lines.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeLine(productId)}
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-black/35 hover:bg-red-50 hover:text-red-500"
                        aria-label={t('step13c.remove_recipe_aria', 'Quitar {nombre}', { nombre: lineName })}
                      >
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </button>
                    )}
                  </div>

                  <div className="mt-2 grid grid-cols-[1fr_auto_auto] items-center gap-2 sm:gap-3">
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      <button
                        type="button"
                        onClick={() => updateQty(productId, qty - 1)}
                        className="flex h-8 w-8 items-center justify-center rounded-full bg-[#E8F6FA] text-[#008BBC] disabled:opacity-35"
                        disabled={qty <= 1}
                        aria-label={t('step13c.decrease_recipe_qty', 'Reducir unidades')}
                      >
                        <Minus className="h-4 w-4" aria-hidden="true" />
                      </button>
                      <input
                        value={qty}
                        onChange={(e) => updateQty(productId, Number(e.target.value))}
                        inputMode="numeric"
                        className="h-8 w-10 rounded-lg border border-gray-200 text-center font-fredoka text-sm font-bold sm:w-12"
                        aria-label={t('step13c.recipe_qty', 'Unidades')}
                      />
                      <button
                        type="button"
                        onClick={() => updateQty(productId, qty + 1)}
                        className="flex h-8 w-8 items-center justify-center rounded-full bg-[#E8F6FA] text-[#008BBC] disabled:opacity-35"
                        disabled={isSoldOut || atLimit}
                        aria-label={t('step13c.increase_recipe_qty', 'Aumentar unidades')}
                      >
                        <Plus className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </div>

                    {canChangeSize ? (
                      <div className="grid shrink-0 grid-cols-2 rounded-lg bg-[#F7F4EE] p-1">
                        {(['400g', '800g'] as const).map((size) => (
                          <button
                            key={size}
                            type="button"
                            onClick={() => changeSize(productId, size)}
                            disabled={meta.sizeLabel !== size && isSizeSoldOut(meta.recipe, size)}
                            className={`whitespace-nowrap rounded-md px-1.5 py-1 font-fredoka text-xs font-bold disabled:cursor-not-allowed disabled:opacity-35 sm:px-2 ${
                              meta.sizeLabel === size ? 'bg-white text-[#008BBC] shadow-sm' : 'text-black/45'
                            }`}
                          >
                            {size}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <span className="font-poppins text-xs text-black/45">{meta.sizeLabel}</span>
                    )}

                    <div className="text-right sm:min-w-[60px]">
                      {discount > 0 && (
                        <span className="block font-poppins text-[10px] text-black/30 line-through">
                          {formatPrice(lineOriginal)}
                        </span>
                      )}
                      <span className="block font-fredoka text-sm font-bold text-black">
                        {formatPrice(lineDiscounted)}
                      </span>
                    </div>
                  </div>
                  {(isSoldOut || atLimit) && (
                    <p
                      role="status"
                      className={`mt-2 font-poppins text-[11px] font-semibold ${isSoldOut ? 'text-red-600' : 'text-amber-600'}`}
                    >
                      {isSoldOut
                        ? t('step13c.stock_out_hint', 'Agotado')
                        : t('step13c.stock_limit_hint', 'Puedes añadir hasta {n} unidades en este envío', {
                            n: limit ?? 0,
                          })}
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          <div className="sticky bottom-0 -mx-4 space-y-2 border-t border-black/5 bg-white px-4 py-3">
            {lines.length > 0 && (
              <div className="flex items-center justify-between rounded-xl bg-[#FAF7F1] px-3 py-1.5">
                <span className="font-poppins text-xs font-semibold text-black/60">
                  {t('step13c.edit_menus_total_label', 'Total pedido')}
                </span>
                <div className="flex items-baseline gap-2">
                  {discount > 0 && (
                    <span className="font-poppins text-xs text-black/30 line-through">
                      {formatPrice(totalOriginal)}
                    </span>
                  )}
                  <span className="font-fredoka text-base font-black text-black">{formatPrice(totalDiscounted)}</span>
                </div>
              </div>
            )}
            {hasSoldOutLine && (
              <p role="status" className="font-poppins text-[11px] font-semibold text-red-600">
                {canRemoveLines
                  ? t(
                      'step13c.stock_out_blocks_apply',
                      'Quita la receta agotada o cámbiala de talla para poder aplicar los cambios.',
                    )
                  : t(
                      'step13c.stock_out_blocks_apply_no_removal',
                      'Cambia de talla la receta agotada o vuelve a elegir recetas para poder aplicar los cambios.',
                    )}
              </p>
            )}
            {/* Apply on the left; «Restaurar original» shares the line only when there is something to restore. */}
            <div className={`grid gap-2 ${canRestore ? 'grid-cols-2' : 'grid-cols-1'}`}>
              <button
                type="button"
                onClick={apply}
                disabled={hasSoldOutLine || !hasChanges}
                className="w-full rounded-xl bg-[#1a1a1a] py-3 font-fredoka text-sm font-bold text-white hover:bg-black/80 transition-colors disabled:cursor-not-allowed disabled:opacity-35"
              >
                {t('step13c.apply_menu_changes', 'Aplicar cambios')}
              </button>
              {canRestore && (
                <button
                  type="button"
                  onClick={resetDraft}
                  className="w-full rounded-xl border border-[#008BBC]/25 bg-white py-3 font-fredoka text-sm font-bold text-[#008BBC] hover:bg-[#008BBC]/5 transition-colors"
                >
                  {t('step13c.reset_recipes', 'Restaurar original')}
                </button>
              )}
            </div>
            {onGoToRecipes && (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onGoToRecipes();
                }}
                className="w-full rounded-xl border border-gray-200 bg-white py-2.5 font-fredoka text-sm font-bold text-black/60 hover:bg-gray-50 transition-colors"
              >
                {t('step13c.go_to_recipes', 'Volver a elegir recetas')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface PetPackItemProps {
  nombre: string;
  raza?: string | null;
  planLabel: string;
  price: number;
  recipeLines: CheckoutRecipeLine[];
  t: TFn;
  onEdit?: () => void;
  editLabel?: string;
  onRemove?: () => void;
}

function PetPackItem({
  nombre,
  raza,
  planLabel,
  price,
  recipeLines,
  t,
  onEdit,
  editLabel,
  onRemove,
}: PetPackItemProps) {
  const [open, setOpen] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  return (
    <div>
      <div className="flex items-center gap-3 py-2">
        <BreedPhoto breedKey={raza ?? undefined} className="w-10 h-10 flex-shrink-0" alt={nombre} />
        <div className="flex-1 min-w-0">
          <h3 className="font-fredoka text-sm font-bold text-[#1a1a1a] leading-tight">
            {ensurePetNameInLabel(planLabel, nombre)}
          </h3>
          <span className="font-poppins text-xs text-black/50">{formatPrice(price)}</span>
        </div>
        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
          {onRemove && (
            <button
              type="button"
              onClick={() => setConfirmingRemove(true)}
              className="w-7 h-7 rounded-full flex items-center justify-center text-black/35 hover:bg-red-50 hover:text-red-500 transition-colors"
              aria-label={t('step13c.remove_pet_pack_aria', 'Eliminar {nombre} del pedido', { nombre })}
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          )}
          <div className="flex items-center gap-2 font-poppins text-xs font-medium text-[#008BBC]">
            {onEdit && (
              <button type="button" onClick={onEdit} className="hover:underline">
                {editLabel || t('step13c.edit_recipes', 'Editar')}
              </button>
            )}
            {onEdit && (
              <span className="text-black/20" aria-hidden="true">
                ·
              </span>
            )}
            <button type="button" onClick={() => setOpen((v) => !v)} className="hover:opacity-80 transition-opacity">
              {open ? t('step13c.hide_details', 'Ocultar') : t('step13c.show_details', 'Ver detalle')}
              <span className="ml-1">{open ? '▲' : '▼'}</span>
            </button>
          </div>
        </div>
      </div>
      {confirmingRemove && onRemove && (
        <div className="mb-2 rounded-xl border border-red-100 bg-red-50 p-3">
          <p className="font-poppins text-xs font-medium text-red-900">
            {t('step13c.remove_pet_confirm', '¿Borrar a {nombre} del pedido?', { nombre })}
          </p>
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setConfirmingRemove(false)}
              className="rounded-lg px-3 py-1.5 font-poppins text-xs font-semibold text-gray-600 hover:bg-white"
            >
              {t('step13c.cancel', 'Cancelar')}
            </button>
            <button
              type="button"
              onClick={() => {
                setConfirmingRemove(false);
                onRemove();
              }}
              className="rounded-lg bg-red-500 px-3 py-1.5 font-poppins text-xs font-semibold text-white hover:bg-red-600"
            >
              {t('step13c.remove_pet_confirm_cta', 'Borrar')}
            </button>
          </div>
        </div>
      )}
      {open && <RecipeLinesList lines={recipeLines} t={t} />}
    </div>
  );
}

const CartSummary: React.FC<CartSummaryProps> = ({
  t,
  isTransition,
  transitionScheduleDays,
  INITIATION_DAYS,
  SUBSCRIPTION_DAYS,
  INITIATION_PACK_DISCOUNT,
  MONTHLY_SUBSCRIPTION_DISCOUNT,
  initiationPrice,
  initiationOriginalPrice,
  monthlyDiscountedPrice,
  supplementsOneOffTotal,
  todayTotal,
  shippingPrice,
  shippingUnavailable = false,
  shippingAddressPending = false,
  shippingControl,
  monthlyShippingFree = false,
  monthlyShippingPrice = 0,
  discountAmount,
  dailyCost,
  savings,
  effectiveRecipeLines,
  hasSupplements,
  selectedSupplements,
  supplementRules,
  donateRecurringAmount,
  donateOneTimeAmount,
  donateCustomAmount,
  donationMode,
  updateFormData,
  onEditRecipes,
  currentDistribution,
  recommendedDistribution,
  onUpdateCurrentDistribution,
  onEditCompletedPetPack,
  onRemoveCompletedPetPack,
  onRemoveCurrentPetPack,
  isMultiPet = false,
  petsCompleted = [],
  currentNombre = '',
  currentRaza = null,
  recipes = [],
  multiPetTotals,
  stockLimits,
  onOpenEditMenus,
  canRemoveRecipes = true,
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [showMonthlyDetails, setShowMonthlyDetails] = useState(false);
  const [editMenusOpen, setEditMenusOpen] = useState(false);

  const openEditMenus = () => {
    onOpenEditMenus?.();
    setEditMenusOpen(true);
  };

  const handleRemoveSupplement = (id: string) => {
    const remainingIds = selectedSupplements.filter((sid) => sid !== id);
    const remainingTotal = remainingIds.reduce((acc, sid) => {
      const s = supplementRules.find((x) => x.id === sid || x.external_variant_id === sid);
      return acc + (s?.price_eur ?? 0);
    }, 0);
    const remainingVariantIds = getSupplementVariantIds(remainingIds, supplementRules);
    updateFormData({
      selectedSupplements: remainingIds,
      selectedSupplementVariantIds: remainingVariantIds,
      supplementsOneOffTotal: Number(remainingTotal.toFixed(2)),
    });
  };

  const displayTotal = todayTotal;

  const displayMonthly = isMultiPet && multiPetTotals ? multiPetTotals.totalMonthly : monthlyDiscountedPrice;
  const monthlyPetLines = isMultiPet
    ? [
        ...petsCompleted.map((pet) => ({
          id: pet.id,
          name: pet.nombre,
          raza: pet.raza,
          price: pet.pricing?.monthlyDiscountedPrice ?? pet.formData.monthlyPlanPrice ?? 0,
        })),
        {
          id: 'current',
          name: currentNombre || t('step13c.current_pet_fallback', 'Mascota actual'),
          raza: currentRaza,
          price: monthlyDiscountedPrice,
        },
      ].filter((line) => line.price > 0)
    : [];

  const displayDailyLabelHidden = isMultiPet;
  const getTransitionPackLabel = (nombre: string) =>
    t('step13c.transition_pack_label', 'Pack de iniciación de {nombre}', { nombre });
  const getFullPackLabel = (nombre: string) => t('step13c.full_pack_label', 'Plan completo de {nombre}', { nombre });
  const currentPetName = currentNombre || t('step13c.current_pet_fallback', 'Mascota actual');
  const transitionPackLabel = getTransitionPackLabel(currentPetName);
  const fullPackLabel = getFullPackLabel(currentPetName);
  const currentOrderDiscount = isTransition ? INITIATION_PACK_DISCOUNT : MONTHLY_SUBSCRIPTION_DISCOUNT;
  const petOrderDiscounts = petsCompleted.map((pet) =>
    discountForStart(pet.formData.subscriptionStart, INITIATION_PACK_DISCOUNT, MONTHLY_SUBSCRIPTION_DISCOUNT),
  );
  const allOrderDiscounts = isMultiPet ? [...petOrderDiscounts, currentOrderDiscount] : [currentOrderDiscount];
  const uniqueOrderDiscounts = Array.from(new Set(allOrderDiscounts.map(roundedDiscount)));
  const hasMixedOrderDiscounts = isMultiPet && uniqueOrderDiscounts.length > 1;
  const sharedOrderDiscount = uniqueOrderDiscounts.length === 1 ? uniqueOrderDiscounts[0] : null;
  const multiPetOriginalInitiation =
    isMultiPet && multiPetTotals
      ? petsCompleted.reduce((acc, pet, idx) => {
          const discounted = pet.pricing?.initiationPrice ?? pet.formData.initiationPackPrice ?? 0;
          const discount = petOrderDiscounts[idx] ?? INITIATION_PACK_DISCOUNT;
          return acc + (discount < 1 ? discounted / (1 - discount) : discounted);
        }, initiationOriginalPrice)
      : 0;
  const multiPetDiscountAmount =
    isMultiPet && multiPetTotals ? Number((multiPetOriginalInitiation - multiPetTotals.totalInitiation).toFixed(2)) : 0;
  const orderDiscountAmount = isMultiPet ? multiPetDiscountAmount : initiationOriginalPrice - initiationPrice;
  const showOrderDiscount = orderDiscountAmount > 0;
  const canEditCurrentMenus = Boolean(
    currentDistribution &&
    Object.keys(currentDistribution).length > 0 &&
    recipes.length > 0 &&
    onUpdateCurrentDistribution,
  );

  const discountLineLabel = (() => {
    if (hasMixedOrderDiscounts) {
      return t('step13c.discount_total', 'Descuento aplicado');
    }
    const pct = Math.round((sharedOrderDiscount ?? currentOrderDiscount) * 100);
    const isInitiationDiscount =
      roundedDiscount(sharedOrderDiscount ?? currentOrderDiscount) === roundedDiscount(INITIATION_PACK_DISCOUNT);
    return isInitiationDiscount
      ? t('step13c.discount_label', 'Dto. suscripción -{pct}%', { pct })
      : t('step13c.discount_monthly', 'Dto. suscripción -{pct}%', { pct });
  })();

  return (
    <div className="order-3 lg:order-none lg:sticky lg:top-4 lg:self-start">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="bg-gray-50 px-5 py-3">
          <h2 className="font-fredoka text-lg font-bold text-[#1a1a1a]">{t('step13c.cart_title', 'Tu pedido')}</h2>
        </div>

        <div className="p-5 space-y-4">
          {/* Pack section */}
          {isMultiPet ? (
            <div className="space-y-1 divide-y divide-gray-100">
              {petsCompleted.map((pet) => (
                <PetPackItem
                  key={pet.id}
                  nombre={pet.nombre}
                  raza={pet.raza}
                  planLabel={
                    pet.formData.subscriptionStart === 'full'
                      ? getFullPackLabel(pet.nombre)
                      : getTransitionPackLabel(pet.nombre)
                  }
                  price={pet.pricing?.initiationPrice ?? 0}
                  recipeLines={
                    pet.formData.initiationPackDistribution
                      ? buildPetRecipeLines(
                          pet.formData.initiationPackDistribution as Record<string, number>,
                          recipes,
                          discountForStart(
                            pet.formData.subscriptionStart,
                            INITIATION_PACK_DISCOUNT,
                            MONTHLY_SUBSCRIPTION_DISCOUNT,
                          ),
                          pet.recipeNames,
                        )
                      : []
                  }
                  t={t}
                  onEdit={onEditCompletedPetPack ? () => onEditCompletedPetPack(pet.id) : undefined}
                  onRemove={onRemoveCompletedPetPack ? () => onRemoveCompletedPetPack(pet.id) : undefined}
                />
              ))}
              <PetPackItem
                nombre={currentPetName}
                raza={currentRaza}
                planLabel={isTransition ? transitionPackLabel : fullPackLabel}
                price={initiationPrice}
                recipeLines={effectiveRecipeLines}
                t={t}
                onEdit={canEditCurrentMenus ? openEditMenus : onEditRecipes}
                editLabel={canEditCurrentMenus ? t('step13c.edit_menus_cta', 'Editar') : undefined}
                onRemove={onRemoveCurrentPetPack}
              />
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-3 py-1">
                <img
                  src={isTransition ? '/images/funnel/pedido-iniciacion.png' : '/images/funnel/pedido-completo.png'}
                  alt={isTransition ? transitionPackLabel : fullPackLabel}
                  className="w-14 h-14 rounded-lg object-cover bg-[#FAF7F1] flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <h3 className="font-fredoka text-base font-bold text-[#1a1a1a] leading-tight">
                    {isTransition ? transitionPackLabel : fullPackLabel}
                  </h3>
                  <span className="font-poppins text-xs text-black/50">
                    {t('step13c.days_suffix', '{days} días', {
                      days: isTransition ? INITIATION_DAYS : SUBSCRIPTION_DAYS,
                    })}
                  </span>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  {/* Precio del pack en su línea: sin él no se ve el desglose
                      (pack vs extras). Es el precio ANTES del dto. de bienvenida,
                      igual que los suplementos muestran el suyo y el subtotal
                      suma ambos (el dto. se resta aparte más abajo). */}
                  <span className="font-fredoka text-base font-bold text-[#1a1a1a] whitespace-nowrap">
                    {formatPrice(initiationOriginalPrice)}
                  </span>
                  <div data-testid="pack-detail-actions" className="flex items-center gap-3">
                    {showDetails && canEditCurrentMenus && (
                      <button
                        type="button"
                        onClick={openEditMenus}
                        className="font-poppins text-xs text-[#008BBC] font-medium hover:underline"
                      >
                        {t('step13c.edit_menus_cta', 'Editar')}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setShowDetails((v) => !v)}
                      className="font-poppins text-xs text-[#008BBC] font-medium hover:opacity-80 transition-opacity"
                    >
                      {showDetails ? t('step13c.hide_details', 'Ocultar') : t('step13c.show_details', 'Ver detalle')}
                      <span className="ml-1">{showDetails ? '▲' : '▼'}</span>
                    </button>
                  </div>
                </div>
              </div>
              {showDetails && <RecipeLinesList lines={effectiveRecipeLines} t={t} />}
            </div>
          )}

          {(donateRecurringAmount !== null || donateOneTimeAmount !== null) && (
            <div>
              <h3 className="font-poppins text-xs font-semibold text-black/40 uppercase tracking-wide mb-2">
                {t('step13c.donation_section_label', 'Donación')}
              </h3>
              <div className="space-y-1">
                {donationMode === 'recurring' && donateRecurringAmount !== null && (
                  <div className="flex items-center gap-2 bg-gray-50 rounded-md px-2 py-1">
                    <span className="text-base flex-shrink-0">🐾</span>
                    <span className="flex-1 font-poppins text-xs text-black truncate">
                      {t('step13c.donation_line_recurring', 'Protectora de Animales · cada mes')}
                    </span>
                    <span className="font-fredoka text-xs font-bold text-black flex-shrink-0">
                      {donateRecurringAmount === 'custom' && donateCustomAmount
                        ? `${donateCustomAmount}€/mes`
                        : `${donateRecurringAmount}€/mes`}
                    </span>
                  </div>
                )}
                {donationMode === 'one-time' && donateOneTimeAmount !== null && (
                  <div className="flex items-center gap-2 bg-gray-50 rounded-md px-2 py-1">
                    <span className="text-base flex-shrink-0">🐾</span>
                    <span className="flex-1 font-poppins text-xs text-black truncate">
                      {t('step13c.donation_line_onetime', 'Protectora de Animales · única')}
                    </span>
                    <span className="font-fredoka text-xs font-bold text-black flex-shrink-0">
                      {donateOneTimeAmount === 'custom' && donateCustomAmount
                        ? `${donateCustomAmount} €`
                        : `${donateOneTimeAmount} €`}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {hasSupplements && (
            <div>
              <h3 className="font-poppins text-xs font-semibold text-black/40 uppercase tracking-wide mb-2">
                {t('step13c.extras_label', 'Suplementos')}
              </h3>
              <div className="space-y-1">
                {selectedSupplements.map((id) => {
                  const sup = supplementRules.find((s) => s.id === id || s.external_variant_id === id);
                  if (!sup) return null;
                  const shortName = (sup.name || '').replace(/^Suplemento\s+/i, '').replace(/\s*\([^)]*\)\s*$/, '');
                  return (
                    <div key={id} className="flex items-center gap-2 bg-gray-50 rounded-md px-2 py-1">
                      <div className="w-7 h-7 rounded bg-white overflow-hidden flex-shrink-0 flex items-center justify-center">
                        {sup.image_url ? (
                          <img src={sup.image_url} alt={shortName} className="w-full h-full object-contain" />
                        ) : (
                          <span className="text-sm">{sup.emoji || '💊'}</span>
                        )}
                      </div>
                      <span className="flex-1 font-poppins text-xs text-black truncate">{shortName}</span>
                      <span className="font-fredoka text-xs font-bold text-black flex-shrink-0">
                        {formatPrice(sup.price_eur)}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveSupplement(id)}
                        aria-label={t('step13c.remove_supplement_aria', 'Quitar {nombre} del pedido', {
                          nombre: shortName,
                        })}
                        className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-black/35 transition-colors hover:bg-red-50 hover:text-red-500"
                      >
                        <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="border-t border-gray-100 pt-3 space-y-2 font-poppins">
            {!isMultiPet && (
              <div className="flex justify-between text-sm">
                <span className="text-black/50">{t('step13c.products', 'Productos')}</span>
                <span className="text-black">{formatPrice(initiationOriginalPrice + supplementsOneOffTotal)}</span>
              </div>
            )}

            {showOrderDiscount && (
              <div className="flex justify-between text-sm">
                <span className="text-[#008BBC] font-bold">{discountLineLabel}</span>
                <span className="text-[#008BBC] font-bold">-{formatPrice(orderDiscountAmount)}</span>
              </div>
            )}

            {/* Products minus discounts: the amount the free-shipping threshold is compared against. */}
            {!isMultiPet && showOrderDiscount && (
              <div className="flex justify-between text-sm">
                <span className="text-black/50">{t('step13c.subtotal', 'Subtotal')}</span>
                <span className="text-black">
                  {formatPrice(initiationOriginalPrice + supplementsOneOffTotal - orderDiscountAmount)}
                </span>
              </div>
            )}

            <div className="text-sm">
              <div className="flex justify-between gap-3">
                <span className="text-[#008BBC] font-bold">{t('step13c.shipping', 'Envío')}</span>
                {shippingAddressPending ? (
                  <span className="text-right font-poppins text-xs text-black/50">
                    {t('step13c.shipping_enter_address', 'Introduce tu código postal')}
                  </span>
                ) : (
                  <span className="text-[#008BBC] font-bold">
                    {shippingUnavailable
                      ? t('step13c.shipping_unavailable', 'No disponible')
                      : shippingPrice > 0
                        ? formatPrice(shippingPrice)
                        : t('step13c.free', 'Gratis')}
                  </span>
                )}
              </div>
              {shippingControl}
            </div>

            <div className="flex justify-between items-center border-t border-gray-100 pt-3">
              <span className="font-fredoka font-bold text-base text-black">{t('step13c.total', 'Total')}</span>
              <span className="font-fredoka text-2xl font-black text-black">{formatPrice(displayTotal)}</span>
            </div>

            {!displayDailyLabelHidden && (
              <p className="text-center font-poppins text-sm text-[#008BBC] font-medium">
                {t('step13c.per_day', '{amount}/día', { amount: formatPrice(dailyCost) })}
                {initiationOriginalPrice - initiationPrice > 0 &&
                  ` ${t('step13c.discount_included', '(dto incluido)')}`}
              </p>
            )}

            {!isMultiPet && savings > 0 && (
              <p className="text-center font-poppins text-sm text-[#008BBC] font-semibold">
                {t('step13c.savings', 'Ahorras {amount}', { amount: formatPrice(savings) })}
              </p>
            )}
          </div>
        </div>
      </div>

      {editMenusOpen && canEditCurrentMenus && currentDistribution && onUpdateCurrentDistribution && (
        <EditMenusModal
          t={t}
          recipes={recipes}
          initialDistribution={currentDistribution}
          recommendedDistribution={recommendedDistribution}
          stockLimits={stockLimits}
          canRemoveLines={canRemoveRecipes}
          onClose={() => setEditMenusOpen(false)}
          onApply={(distribution) => {
            onUpdateCurrentDistribution(distribution);
            setEditMenusOpen(false);
          }}
          raza={currentRaza}
          nombre={currentNombre}
          packDays={isTransition ? INITIATION_DAYS : SUBSCRIPTION_DAYS}
          discount={currentOrderDiscount}
          onGoToRecipes={onEditRecipes}
        />
      )}

      {isTransition && (
        <div className="bg-white rounded-2xl border-2 border-[#008BBC]/30 shadow-sm p-4 mt-4">
          <div className="flex items-start justify-between mb-1">
            <p className="font-poppins text-xs text-black/40">
              {t('step13c.monthly_from', 'Después del primer pedido')}
            </p>
            <span className="font-fredoka text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#008BBC]/10 text-[#008BBC]">
              {t('step13c.monthly_discount_badge', '-{pct}% suscripcion', {
                pct: Math.round(MONTHLY_SUBSCRIPTION_DISCOUNT * 100),
              })}
            </span>
          </div>
          <h3 className="font-fredoka text-lg font-bold text-black">{t('step13c.monthly_plan', 'Plan mensual')}</h3>
          <p className="font-fredoka text-xl font-bold text-[#008BBC]">
            {formatPrice(displayMonthly)}
            <span className="text-sm font-normal text-black/40">{t('step13c.per_month_suffix', '/mes')}</span>
          </p>
          <div className="mt-1 flex items-center justify-between gap-3">
            <p className="font-poppins text-xs text-black">
              {monthlyShippingFree
                ? t('step13c.monthly_shipping_free', 'Envío gratis · Sin compromiso')
                : monthlyShippingPrice > 0
                  ? t('step13c.monthly_shipping_priced', 'Envío {price} · Sin compromiso', {
                      price: formatPrice(monthlyShippingPrice),
                    })
                  : t('step13c.monthly_details_nocommit', 'Sin compromiso')}
            </p>
            {monthlyPetLines.length > 1 && (
              <button
                type="button"
                onClick={() => setShowMonthlyDetails((value) => !value)}
                className="shrink-0 font-poppins text-xs font-semibold text-[#008BBC] hover:opacity-80 transition-opacity"
                aria-expanded={showMonthlyDetails}
              >
                {showMonthlyDetails ? t('step13c.hide_details', 'Ocultar') : t('step13c.show_details', 'Ver detalle')}
                <span className="ml-1">{showMonthlyDetails ? '▲' : '▼'}</span>
              </button>
            )}
          </div>
          {showMonthlyDetails && monthlyPetLines.length > 1 && (
            <div className="mt-3 space-y-1.5 border-t border-[#008BBC]/15 pt-3">
              {monthlyPetLines.map((line) => (
                <div
                  key={line.id}
                  className="flex items-center justify-between gap-3 rounded-lg bg-[#F7F4EE] px-3 py-2"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <BreedPhoto breedKey={line.raza ?? undefined} alt={line.name} className="h-8 w-8 flex-shrink-0" />
                    <span className="min-w-0 truncate font-poppins text-xs font-medium text-black/70">{line.name}</span>
                  </div>
                  <span className="shrink-0 font-fredoka text-sm font-bold text-[#1a1a1a]">
                    {formatPrice(line.price)}
                    <span className="text-[11px] font-normal text-black/40">
                      {t('step13c.per_month_suffix', '/mes')}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CartSummary;
